import { Alert, Box, Button, CircularProgress, Link, Typography } from '@mui/material'
import CameraControls from 'camera-controls'
import { ApplicationTypes, type EnhancedDomain } from 'jms-domain'
import { useObservableEagerState } from 'observable-hooks'
import { useCallback, useRef, useState } from 'react'
import { OrthographicCamera, PerspectiveCamera, type Scene, Sphere, Vector3 } from 'three'
import { match } from 'ts-pattern'
import { v4 } from 'uuid'

import { useLogger } from '@salsita-ai/platform-project-sdk/react'
import { Dialog, PhxIcon } from '@salsita-ai/platform-project-sdk/ui'

import { configuratorInterface } from '../systems/configurator-interface'
import { FINALIZE_FORM_ID, FinalizeForm } from './FinalizeForm'

const WALL_IMAGE_WIDTH = 1280
const WALL_IMAGE_HEIGHT = 720

const GLAMOUR_IMAGE_WIDTH = 1280
const GLAMOUR_IMAGE_HEIGHT = 1280

interface SuccessPageProps {
  pdfLink: string
  onClose: () => void
}

const SuccessPage = (props: SuccessPageProps) => {
  const { pdfLink, onClose } = props

  return (
    <Dialog.ContentLayout
      controlsPosition="end"
      header={
        <Box
          component="div"
          sx={theme => ({
            backgroundColor: theme.palette.background.default,
            aspectRatio: '2.9',
            display: 'grid',
            placeItems: 'center',
            color: 'green',
          })}
        >
          <PhxIcon name="CheckCircle" style={{ fontSize: 200 }} />
        </Box>
      }
      body={
        <>
          <Typography variant="h4" gutterBottom id="success-dialog-title">
            Successfully Submitted
          </Typography>
          <Typography variant="body1" gutterBottom id="success-dialog-description">
            Email with PDF attachment sent to supplied email addresses. The PDF is also available for{' '}
            <Link href={pdfLink} target="_blank">
              download
            </Link>
            .
          </Typography>
        </>
      }
      controls={
        <Button variant="text" onClick={onClose}>
          Back to Summary
        </Button>
      }
    />
  )
}

interface FinalizePageProps {
  backNavigationState: ApplicationTypes.NavigationState
}

export const FinalizePage = (props: FinalizePageProps) => {
  const { backNavigationState } = props
  const logger = useLogger()

  const sceneRef = useRef<Scene | null>(null)

  const { configurationId, navigationSystem, storeSystem, rootEntityId } = configuratorInterface.useDomain()
  const { cameraControlSystem } = configuratorInterface.useConfigurator()
  const apiClient = configuratorInterface.useAuthorizedClient()
  const { configurationPersistSystem } = configuratorInterface.useDomainApplication()
  const { imageRenderingSystem } = configuratorInterface.useConfigurator()

  const activeNavigationState = useObservableEagerState(navigationSystem.activeNavigationState$)
  const $room = configuratorInterface.useEntityState(rootEntityId)

  const [isPending, setIsPending] = useState(false)
  const [finalizationResult, setFinalizationResult] = useState<{ pdfLink: string } | null>(null)
  const [error, setError] = useState<React.ReactNode>(null)

  const handleBack = useCallback(() => {
    storeSystem.stageAndCommit([navigationSystem.getNavigationUpdate(backNavigationState)])
  }, [navigationSystem, storeSystem, backNavigationState])

  const generateFullImage = useCallback(
    async (finalizationUuid: string) => {
      await imageRenderingSystem.generateImages(finalizationUuid, [
        {
          name: 'imagefull',
          width: GLAMOUR_IMAGE_WIDTH,
          height: GLAMOUR_IMAGE_HEIGHT,
          getCamera: async scene => {
            sceneRef.current = scene

            const camera = new PerspectiveCamera(45, GLAMOUR_IMAGE_WIDTH / GLAMOUR_IMAGE_HEIGHT, 0.1, 100000)
            const controls = new CameraControls(camera)
            const {
              $boundingBoxMinX = 0,
              $boundingBoxMaxX = 0,
              $boundingBoxMinY = 0,
              $boundingBoxMaxY = 0,
              $boundingBoxMinZ = 0,
              $boundingBoxMaxZ = 0,
            } = $room

            const boundingBox = new Sphere(
              new Vector3(
                ($boundingBoxMaxX + $boundingBoxMinX) / 2,
                ($boundingBoxMaxY + $boundingBoxMinY) / 2,
                ($boundingBoxMaxZ + $boundingBoxMinZ) / 2
              ),
              Math.max(
                ($boundingBoxMaxX - $boundingBoxMinX) / 2,
                ($boundingBoxMaxY - $boundingBoxMinY) / 2,
                ($boundingBoxMaxZ - $boundingBoxMinZ) / 2
              )
            )

            let positionX: number
            let positionY: number
            let positionZ: number
            let lookAtX: number
            let lookAtY: number
            let lookAtZ: number
            if ($room.shape === 'L') {
              const squareDistance = Math.max($boundingBoxMaxX - $boundingBoxMinX, $boundingBoxMaxZ - $boundingBoxMinZ)

              positionX = squareDistance
              positionY = $boundingBoxMaxY
              positionZ = squareDistance
              lookAtX = $boundingBoxMinX
              lookAtY = ($boundingBoxMaxY + $boundingBoxMinY) / 2
              lookAtZ = $boundingBoxMinZ
            } else if ($room.shape === 'U') {
              positionX = ($boundingBoxMaxX - $boundingBoxMinX) / 2
              positionY = $boundingBoxMaxY
              positionZ = Math.max($boundingBoxMaxX - $boundingBoxMinX, $boundingBoxMaxZ - $boundingBoxMinZ)
              lookAtX = ($boundingBoxMaxX - $boundingBoxMinX) / 2
              lookAtY = ($boundingBoxMaxY + $boundingBoxMinY) / 2
              lookAtZ = $boundingBoxMinZ
            } else {
              positionX = 0
              positionY = ($boundingBoxMaxY + $boundingBoxMinY) / 2
              positionZ = $room.$wallB.length
              lookAtX = 0
              lookAtY = ($boundingBoxMaxY + $boundingBoxMinY) / 2
              lookAtZ = 0
            }

            await controls.setLookAt(positionX, positionY, positionZ, lookAtX, lookAtY, lookAtZ, false)

            await controls.fitToSphere(boundingBox, false)
            controls.update(1000000)

            return camera
          },
        },
      ])
    },
    [imageRenderingSystem, $room]
  )

  const generateWallImage = useCallback(
    async (finalizationUuid: string, $wall: EnhancedDomain.$Wall) => {
      await imageRenderingSystem.generateImages(finalizationUuid, [
        {
          name: `imagewall${$wall.wallType.toLowerCase()}`,
          width: WALL_IMAGE_WIDTH,
          height: WALL_IMAGE_HEIGHT,
          getCamera: async scene => {
            const mesh = scene.getObjectByName($wall.id)
            if (mesh == null) {
              throw new Error('Room mesh not found!')
            }

            // Change camera position to fit the wall manually.
            // Ideally it should fit autimatically with `camera-controls`,
            // but rotation did not work with it for some reason.
            const paddingX = 500
            const bottomPadding = 200
            const aspect = WALL_IMAGE_WIDTH / WALL_IMAGE_HEIGHT

            const minX = match($wall.wallType)
              .with('A', () => -$wall.$wallObjectZ)
              .with('B', () => $wall.$wallObjectX)
              .with('C', () => $wall.$wallObjectZ)
              .exhaustive()

            const maxX = minX + $wall.length
            let left = minX - paddingX
            let right = maxX + paddingX
            let top = 2500
            const bottom = -bottomPadding

            const viewHeight = top - bottom
            const viewWidth = right - left
            if (viewWidth / viewHeight < aspect) {
              const extraWidthPadding = (viewHeight * aspect - viewWidth) / 2
              left -= extraWidthPadding
              right += extraWidthPadding
            } else {
              const extraHeightPadding = viewWidth / aspect - viewHeight
              top += extraHeightPadding
            }

            const camera = new OrthographicCamera(left, right, top, bottom, 0.1, 100000)

            // Match wall rotation.
            // Rotating camera for some reason does not rotate labels of dimension indicators..
            // So this is probably a hack, but it works and I have no other idea how to do it.
            scene.rotation.set(0, -mesh.rotation.y, 0)
            camera.position.set(0, 0, 0)

            await new Promise(resolve => setTimeout(resolve, 1000))

            return camera
          },
        },
      ])
    },
    [imageRenderingSystem]
  )

  // Show only the wall with the given ID, hide all others.
  // When wallId is false, disable hiding and show all walls.
  const showSingleWall = useCallback(
    async (wallId: EnhancedDomain.$Wall['id'] | false) => {
      const updates = $room.$walls.map($w =>
        configuratorInterface.updates.DomainEntityModification({
          entityId: $w.id,
          stateDelta: {
            hidden: wallId !== false && $w.id !== wallId,
          },
          updateTag: 'HideWallDuringFinalization',
        })
      )

      storeSystem.stageAndCommit(updates)
      await new Promise(resolve => setTimeout(resolve, 10))
    },
    [$room, storeSystem]
  )

  const handleSubmit = useCallback(
    async (data: unknown) => {
      const finalizationUuid = v4()

      setIsPending(true)

      cameraControlSystem.resetCamera()

      try {
        // TODO: Add validation
        // const parsedData = FinalizeData.parse(data)
        const parsedData = data as unknown as ApplicationTypes.FinalizeData

        await configurationPersistSystem.save()

        storeSystem.stageAndCommit([
          navigationSystem.getNavigationUpdate(ApplicationTypes.NavigationState.FinalizeGlamour),
        ])

        await new Promise(resolve => setTimeout(resolve, 1000))

        await generateFullImage(finalizationUuid)

        storeSystem.stageAndCommit([navigationSystem.getNavigationUpdate(ApplicationTypes.NavigationState.Finalize)])

        await new Promise(resolve => setTimeout(resolve, 1000))

        for (const $wall of $room.$walls) {
          if ($wall.$active) {
            await showSingleWall($wall.id)
            await generateWallImage(finalizationUuid, $wall)
          }
        }

        await showSingleWall(false)

        if (sceneRef.current) {
          sceneRef.current.rotation.set(0, 0, 0)
        }

        await apiClient.finalize(configurationId, finalizationUuid, parsedData)
        const response = await apiClient.waitForFinalizationToComplete(configurationId, finalizationUuid)

        if (response?.finalizationErrorMessage) {
          setError(<>{response.finalizationErrorMessage}</>)
        } else if (response?.pdfLink) {
          setFinalizationResult({
            pdfLink: response.pdfLink,
          })
        } else {
          throw new Error('PDF link not found in response')
        }
      } catch (error) {
        setError(<>Configuration details could not be sent. Please try again later.</>)

        if (error instanceof Error) {
          logger.error('Finalization failed', {}, error)
        } else {
          logger.error('Finalization failed', { error })
        }
      } finally {
        setIsPending(false)
      }
    },
    [
      apiClient,
      logger,
      configurationId,
      configurationPersistSystem,
      generateWallImage,
      showSingleWall,
      $room,
      generateFullImage,
      storeSystem,
      navigationSystem,
      cameraControlSystem,
    ]
  )

  if (
    activeNavigationState !== ApplicationTypes.NavigationState.Finalize &&
    activeNavigationState !== ApplicationTypes.NavigationState.FinalizeGlamour
  ) {
    return null
  }

  if (finalizationResult) {
    return <SuccessPage pdfLink={finalizationResult.pdfLink} onClose={handleBack} />
  }

  return (
    <Dialog.ContentLayout
      controlsPosition="end"
      body={
        <>
          <FinalizeForm isPending={isPending} onSubmit={handleSubmit} />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </>
      }
      controls={
        <>
          <Button variant="text" onClick={handleBack}>
            Back to Summary
          </Button>
          <Button
            variant="contained"
            type="submit"
            form={FINALIZE_FORM_ID}
            startIcon={
              isPending ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <PhxIcon name="PaperPlaneTilt" />
            }
          >
            Finish
          </Button>
        </>
      }
    />
  )
}
